import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTS = [
  "MS Pipes", "MS Square Pipes", "MS Angles", "TMT Rods", "MS Flats", "MS Channels",
  "GI Items", "Iron Scrap", "Plastic Scrap", "Vehicle Scrap", "Other",
];

const fieldCls =
  "w-full bg-transparent border border-[#262626] px-4 py-4 text-sm text-[#f5f5f5] placeholder:text-[#525252] focus:border-[#a3a3a3] focus:outline-none transition-colors duration-300 rounded-none";

export default function QuoteForm() {
  const [form, setForm] = useState({ name: "", phone: "", product: "", quantity: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.product) {
      toast.error("Please fill in your name, phone and product.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request sent. We'll call you back shortly.");
      setForm({ name: "", phone: "", product: "", quantity: "", message: "" });
    } catch {
      toast.error("Could not send your request. Please call or WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="border-b border-[#262626] px-6 md:px-12 py-32 md:py-48" data-testid="quote-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p className="text-xs uppercase tracking-[0.35em] text-[#525252] mb-6">Quote Request</p>
          <p
            className="mb-8 font-display uppercase leading-[0.85] tracking-tight text-6xl lg:text-8xl text-steel whitespace-nowrap select-none"
            data-testid="quote-get-quote-display"
          >
            Get a Quote
          </p>
          <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl uppercase leading-[0.9] tracking-tight text-steel mb-8">
            Tell Us What You Need
          </h2>
          <p className="text-sm md:text-base text-[#a3a3a3] leading-relaxed max-w-md">
            Send your requirement — product, quantity, sizes if you know them. It
            lands straight in our inbox and we get back to you with today's rate.
            Selling scrap? Tell us what you've got and roughly how much.
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#262626] border border-[#262626]"
          data-testid="quote-form"
        >
          <div className="bg-[#050505] p-6 md:p-8">
            <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] mb-3">Your Name *</label>
            <input
              className={fieldCls}
              value={form.name}
              onChange={set("name")}
              placeholder="Full name"
              data-testid="quote-name-input"
            />
          </div>
          <div className="bg-[#050505] p-6 md:p-8">
            <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] mb-3">Phone / WhatsApp *</label>
            <input
              className={fieldCls}
              value={form.phone}
              onChange={set("phone")}
              placeholder="+91 ..."
              data-testid="quote-phone-input"
            />
          </div>
          <div className="bg-[#050505] p-6 md:p-8">
            <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] mb-3">Product Needed *</label>
            <select
              className={`${fieldCls} appearance-none cursor-pointer`}
              value={form.product}
              onChange={set("product")}
              data-testid="quote-product-select"
            >
              <option value="" disabled className="bg-[#0a0a0a]">Select product</option>
              {PRODUCTS.map((p) => (
                <option key={p} value={p} className="bg-[#0a0a0a]">{p}</option>
              ))}
            </select>
          </div>
          <div className="bg-[#050505] p-6 md:p-8">
            <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] mb-3">Quantity</label>
            <input
              className={fieldCls}
              value={form.quantity}
              onChange={set("quantity")}
              placeholder="e.g. 2 tonnes / 50 lengths"
              data-testid="quote-quantity-input"
            />
          </div>
          <div className="bg-[#050505] p-6 md:p-8 md:col-span-2">
            <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] mb-3">Message</label>
            <textarea
              rows={4}
              className={`${fieldCls} resize-none`}
              value={form.message}
              onChange={set("message")}
              placeholder="Sizes, grade, delivery location, scrap details — anything that helps us quote faster."
              data-testid="quote-message-textarea"
            />
          </div>
          <div className="bg-[#050505] p-6 md:p-8 md:col-span-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#f5f5f5] text-[#050505] text-xs uppercase tracking-[0.25em] py-5 hover:bg-[#d4d4d4] disabled:opacity-50 transition-colors duration-300"
              data-testid="quote-submit-button"
            >
              {loading ? "Sending..." : "Send Quote Request"}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
